import { findDay, findSession, matches } from '@ce/core';
import { trackColor } from '@ce/theme';
import { useTheme } from '../ThemeProvider';
import {
  btn, divider, kicker, matchStyle, portrait, saveStyle, t,
} from '../styles';
import { CardArc } from '../components/Chrome';
import type { ScreenProps } from './types';

/**
 * Session detail.
 *
 * The thumbnail is optional in the CMS. When a session has no image of its own
 * it borrows the portrait of its first guest, and when it has neither it gets
 * the arc motif — a session card should never render as a grey box.
 */
export function SessionDetail({ bundle, state, dispatch }: ScreenProps) {
  const theme = useTheme();
  const session = findSession(bundle, state.sessionId);

  if (!session) {
    return (
      <div className="stagger">
        <div style={{ fontSize: 13, color: t(0.5) }}>That session is no longer in the programme.</div>
        <button
          type="button"
          onClick={() => dispatch({ type: 'go', screen: 'schedule' })}
          style={btn('secondary', { width: '100%', height: 42, marginTop: 14 })}
        >
          Back to schedule
        </button>
      </div>
    );
  }

  const day = findDay(bundle, session.day);
  const guests = session.who
    .map((id) => bundle.talent.find((tt) => tt.id === id))
    .filter((x): x is NonNullable<typeof x> => !!x);
  const hero = session.imageUrl ?? guests[0]?.photoUrl;
  const on = state.saved.includes(session.id);
  const match = matches(state.interests, session.tags);

  // Which level the room sits on, so "Find the room" lands correctly.
  const level =
    bundle.venue.levels.find((l) => l.blocks.some((b) => b.name === session.room))?.id ?? '1';

  return (
    <div className="stagger">
      <button
        type="button"
        onClick={() => dispatch({ type: 'go', screen: 'schedule' })}
        style={btn('ghost', { fontSize: 12.5, margin: '0 0 12px -2px' })}
      >
        ← Schedule
      </button>

      <div
        style={{
          height: 168, borderRadius: 12, overflow: 'hidden', position: 'relative',
          background: 'linear-gradient(150deg, var(--color-accent-900), var(--color-surface) 70%)',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        {hero ? (
          <div style={portrait(hero, 22)} />
        ) : (
          <>
            <CardArc size={260} top={-110} right={-70} opacity={0.2} />
            <CardArc size={170} top={-60} right={-24} opacity={0.13} />
          </>
        )}
        <div
          style={{
            position: 'absolute', inset: 0,
            background:
              'linear-gradient(to top, rgba(var(--color-bg-rgb), 0.95) 0%, rgba(var(--color-bg-rgb), 0.35) 46%, transparent 76%)',
          }}
        />
        <div style={{ position: 'absolute', left: 15, right: 15, bottom: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                ...kicker(9.5, t(0.7), 0.14),
              }}
            >
              <span
                style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: trackColor(theme, session.track),
                }}
              />
              {session.track}
            </span>
            <span style={matchStyle(match)}>Your interests</span>
          </div>
          <h3
            style={{
              fontSize: 22, lineHeight: 1.16, letterSpacing: '-0.02em',
              margin: '9px 0 0', fontWeight: 500,
            }}
          >
            {session.title}
          </h3>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: '500 15px/1.3 var(--font-body)' }}>
            {day.label} · {session.t}–{session.end}
          </div>
          <div style={{ fontSize: 12.5, color: t(0.55), marginTop: 4 }}>{session.room}</div>
        </div>
        <button
          type="button"
          onClick={() => dispatch({ type: 'toggleSave', id: session.id })}
          aria-label={on ? 'Remove from my schedule' : 'Add to my schedule'}
          style={saveStyle(on)}
        >
          {on ? '★' : '☆'}
        </button>
      </div>

      {session.notes && session.notes.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {session.notes.map((note) => (
            <span
              key={note}
              style={{
                display: 'inline-flex', fontSize: 10, padding: '4px 10px', borderRadius: 6,
                background: 'var(--color-neutral-800)', color: 'var(--color-neutral-100)',
              }}
            >
              {note}
            </span>
          ))}
        </div>
      )}

      {session.desc && (
        <p style={{ fontSize: 13.5, lineHeight: 1.68, color: t(0.72), margin: '18px 0 0' }}>
          {session.desc}
        </p>
      )}

      {guests.length > 0 && (
        <>
          <div style={{ ...kicker(), margin: '24px 0 11px' }}>
            {guests.length === 1 ? 'Guest' : 'Guests'}
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {guests.map((guest) => (
              <button
                key={guest.id}
                type="button"
                onClick={() => dispatch({ type: 'openTalent', id: guest.id })}
                style={{
                  display: 'flex', gap: 13, alignItems: 'center', width: '100%', textAlign: 'left',
                  padding: '11px 13px', borderRadius: 10, cursor: 'pointer', border: 0,
                  background: 'var(--color-surface)', color: 'var(--color-text)',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <div
                  style={{
                    flex: 'none', width: 46, height: 46, borderRadius: 8,
                    overflow: 'hidden', background: 'var(--color-neutral-900)',
                  }}
                >
                  <div style={portrait(guest.photoUrl, 20)} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: '500 14.5px/1.2 var(--font-body)' }}>{guest.name}</div>
                  <div style={{ fontSize: 11.5, lineHeight: 1.4, color: t(0.52), marginTop: 4 }}>
                    {guest.role}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: t(0.4), flex: 'none' }}>
                  from {bundle.event.currency}
                  {Math.min(guest.prices.photo, guest.prices.auto)}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button
          type="button"
          onClick={() => dispatch({ type: 'locate', level })}
          style={btn('primary', { flex: 1, height: 44, borderRadius: 10 })}
        >
          Find the room
        </button>
        <button
          type="button"
          onClick={() => dispatch({ type: 'go', screen: 'timeline' })}
          style={btn('secondary', { height: 44, borderRadius: 10 })}
        >
          See the day
        </button>
      </div>
      <div style={{ height: 26, borderColor: divider }} />
    </div>
  );
}
